<template>
    <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
            <span class="text-subtitle-1">{{ title }}</span>
            <v-btn v-if="openInNewTab" :href="src" icon="mdi-open-in-new" rel="noopener" target="_blank"
                   variant="text"/>
        </v-card-title>

        <v-card-text class="py-0">
            <!-- Scrollable container -->
            <div
                ref="scrollRoot"
                :style="{ height, border: '1px solid #E0E0E0', overflow: 'auto', padding: '8px', background: '#f9f9f9' }"
                class="pdf-scroll-root"
            >
                <!-- Canvases for pages -->
                <div
                    v-for="(p, i) in pages"
                    :key="i"
                    class="mb-4"
                    style="display: flex; justify-content: center;"
                >
                    <canvas :ref="el => setCanvasRef(i, el)"/>
                </div>

                <!-- Sentinel at the very end -->
                <div ref="sentinel" style="height: 2px;"></div>
            </div>

            <v-alert v-if="!scrolledToEnd" class="text-start" color="black" density="compact" type="info"
                     variant="text">Please review all pages to continue
            </v-alert>
        </v-card-text>

        <v-card-actions class="d-flex justify-end">
            <v-btn variant="outlined" size="small" @click="sendFeedback(0)">Decline</v-btn>
            <v-btn :disabled="!scrolledToEnd" color="success" size="small" @click="sendFeedback(1)">I Accept</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script setup>
import {ref, reactive, onMounted, onBeforeUnmount, nextTick} from 'vue';
import {getDocument, GlobalWorkerOptions} from 'pdfjs-dist';

// 1) Point to the ESM worker (works in modern bundlers)
GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js`;

// Props
const props = defineProps({
    src: {required: true},   // PDF URL (must allow CORS if cross-origin)
    srcType: {type: String, default: 'url'},
    title: {type: String, default: 'Document'},
    height: {type: String, default: '600px'},
    scale: {type: Number, default: 1.25},
    openInNewTab: {type: Boolean, required: false},
    maxPages: {type: Number, default: 0},  // 0 = render all pages
});
const emits = defineEmits(['signAction']);
// State
const pages = reactive([]);         // placeholders to render canvases
const canvasRefs = ref([]);         // canvas elements per page index
const pdfInstance = ref(null);
const scrollRoot = ref(null);
const sentinel = ref(null);
const timeSpent = ref(performance.now());
const scrolledToEnd = ref(false);
let io = null;

// Helpers
const setCanvasRef = (index, el) => {
    if (el) canvasRefs.value[index] = el;
}

const toUint8 = (data) => {
    if (data instanceof Uint8Array) return data;
    if (data instanceof ArrayBuffer) return new Uint8Array(data);
    // If Blob, convert to ArrayBuffer first (async) - caller should pass bytes ideally
    return null;
}

async function loadAndPrepare() {
    // Load PDF
    let loadingTask = null;
    if (props.srcType === 'url') {
        loadingTask = getDocument({
            url: props.src
        });
    } else {
        const bytes = toUint8(props.src);
        if (!bytes) throw new Error('Invalid binary source for PDF.');
        loadingTask = getDocument({
            data: bytes,
        });
    }

    const pdf = await loadingTask.promise;
    pdfInstance.value = pdf;

    // Determine how many pages to draw
    const total = props.maxPages > 0 ? Math.min(props.maxPages, pdf.numPages) : pdf.numPages;

    // Trigger DOM render of canvases
    pages.splice(0, pages.length, ...Array.from({length: total}, (_, i) => ({index: i + 1})));
    await nextTick();

    // Render pages sequentially
    for (let i = 1; i <= total; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({scale: props.scale});

        const canvas = canvasRefs.value[i - 1];
        if (!canvas) continue;

        const ctx = canvas.getContext('2d', {alpha: false});
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        await page.render({canvasContext: ctx, viewport}).promise;
    }
}

const sendFeedback = (action) => {
    const elapsed = parseFloat(((performance.now() - timeSpent.value) / 1000).toFixed(2));
    emits('signAction', {
        'accept': action,
        'scrolled_to_bottom': scrolledToEnd.value,
        time_spent: elapsed,
    });
}

onMounted(async () => {
    try {
        await loadAndPrepare();

        // Create IntersectionObserver to detect bottom reached
        io = new IntersectionObserver(
            (entries) => {
                if (entries.some(e => e.isIntersecting)) {
                    scrolledToEnd.value = true;
                    io.disconnect();
                }
            },
            {
                root: scrollRoot.value,   // must be the element, not a function
                threshold: 0.01
            }
        );

        if (sentinel.value) io.observe(sentinel.value);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('PDF render error:', err);
    }
});

onBeforeUnmount(() => {
    if (io) io.disconnect();
    if (pdfInstance.value) {
        pdfInstance.value.destroy();
        pdfInstance.value = null;
    }
});
</script>

<style scoped>
.pdf-scroll-root canvas {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    background: #fff;
}
</style>
